import { useCallback, useRef, useState } from "react";
import { useVoiceRecorder, type VoiceRecorderResult } from "../lib/voiceRecorder";

type VoiceRecorderButtonProps = {
  onVoiceReady: (result: VoiceRecorderResult) => void;
  onError: (msg: string) => void;
  disabled?: boolean;
};

function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function VoiceRecorderButton({ onVoiceReady, onError, disabled }: VoiceRecorderButtonProps) {
  const [cancelHint, setCancelHint] = useState(false);
  const pointerStartYRef = useRef<number>(0);
  const cancelledRef = useRef(false);

  const { state, durationMs, start, stop, cancel } = useVoiceRecorder(onVoiceReady, onError);

  const isRecording = state === "recording";
  const isProcessing = state === "processing";

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disabled || isProcessing) return;
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      pointerStartYRef.current = e.clientY;
      cancelledRef.current = false;
      setCancelHint(false);
      void start();
    },
    [disabled, isProcessing, start],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isRecording) return;
      const dy = pointerStartYRef.current - e.clientY; // positive = moved up
      if (dy > 40) {
        setCancelHint(true);
        if (dy > 80 && !cancelledRef.current) {
          cancelledRef.current = true;
          setCancelHint(false);
          cancel();
        }
      } else {
        setCancelHint(false);
      }
    },
    [isRecording, cancel],
  );

  const handlePointerUp = useCallback(() => {
    if (!isRecording) return;
    if (cancelledRef.current) return;
    stop();
  }, [isRecording, stop]);

  const handlePointerCancel = useCallback(() => {
    if (isRecording) cancel();
    setCancelHint(false);
    cancelledRef.current = false;
  }, [isRecording, cancel]);

  return (
    <div className="voice-recorder">
      {isRecording && (
        <div className={`voice-recorder__hint ${cancelHint ? "voice-recorder__hint--cancel" : ""}`}>
          {cancelHint ? "← отмена" : `${formatDuration(durationMs)} ↑ отмена`}
        </div>
      )}
      <button
        type="button"
        className={`voice-recorder__btn${isRecording ? " is-recording" : ""}${isProcessing ? " is-processing" : ""}`}
        aria-label={isRecording ? "Остановить запись" : "Записать голосовое"}
        title={isRecording ? "Отпустите для отправки" : "Зажмите для записи"}
        disabled={disabled || isProcessing}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        // Prevent context menu on long-press (mobile)
        onContextMenu={(e) => e.preventDefault()}
      >
        <span
          aria-hidden="true"
          className="voice-recorder__icon"
          style={{
            maskImage: "url(/emoji/mic.svg)",
            WebkitMaskImage: "url(/emoji/mic.svg)",
          }}
        />
      </button>
    </div>
  );
}
